"use client";

import { useCallback, useRef, useState, type ReactNode } from "react";
import { PricingCard } from "@/components/ui/PricingCard";
import { SegmentToggle } from "@/components/ui/SegmentToggle";
import { Container } from "@/components/ui/Container";
import { trackClickPricingToggle, trackClickTariffSelect } from "@/lib/metrics";
import { siteConfig } from "@/config/site";
import { landingContent } from "@/content/landing";
import { buildAppUrl, featureGuestTranscription } from "@/lib/config";
import { appendUtmToUrl } from "@/lib/utm-forward";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import { usePaddlePricing } from "@/hooks/usePaddlePricing";
import { usePaywallPriceWaitObservability } from "@/hooks/usePaywallPriceWaitObservability";
import { getExtraMinuteBreakdown, getPlanBreakdown } from "@/lib/paddle-pricing";
import { buildPlanPriceView } from "@/lib/plan-price-view";

// Иконки пунктов «В каждом тарифе» в мобильной раскладке. Берутся по ключу из
// копирайта, а не по позиции: список различается по рынкам (у Scriptario нет
// Telegram-бота и API), и привязка по индексу подписала бы «Запись встреч»
// самолётиком Telegram.
const featureIconPaths: Record<string, React.ReactNode> = {
  telegram: <><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4Z" /></>,
  meetings: <><path d="m22 8-6 4 6 4V8Z" /><rect width="14" height="12" x="2" y="6" rx="2" /></>,
  ai: <><path d="M12 3 13.9 8.1 19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" /><path d="M19 16.5 19.8 18.7 22 19.5 19.8 20.3 19 22.5 18.2 20.3 16 19.5 18.2 18.7 19 16.5Z" /></>,
  files: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /></>,
  speakers: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>,
  api: <><path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" /></>,
};

function FeatureIcon({ iconKey }: { iconKey?: string }) {
  const paths = iconKey ? featureIconPaths[iconKey] : null;
  if (!paths) return <CheckIcon />;
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
      {paths}
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#12B829" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M5 12l5 5L20 6" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M4.05 11a8 8 0 1 1 .5 4m-.5 5v-5h5" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
      <path d="M9 12l2 2l4 -4" />
    </svg>
  );
}

function LightningIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="var(--brand-primary)" className="shrink-0">
      <path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13l0-8z" />
    </svg>
  );
}

/**
 * Квадрат выбора периода в мобильном пейволе.
 *
 * Рамка видна всегда — иначе невыбранная карточка выглядит как текст без
 * управления, и неясно, что период можно переключить.
 */
function SelectionBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex size-[20px] shrink-0 items-center justify-center rounded-[6px] border-[1.5px] ${checked ? "bg-[#FF7900] border-[#FF7900]" : "bg-white border-[#CBD5E1]"}`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={checked ? "" : "opacity-0"}>
        <path d="M5 12l5 5L20 6" />
      </svg>
    </span>
  );
}

function StarIcon() {
  return <span className="text-[#FF7900] tracking-[1px]" aria-hidden="true">★★★★★</span>;
}

const DEFAULT_BILLING = "year";

/** `lead` — optional line under the section title (used by the API pages); other pages render unchanged. */
export function PricingV2({ className = "", lead }: { className?: string; lead?: ReactNode }) {
  // Все сайты открывают тарифы на годовых ценах.
  const [billing, setBilling] = useState(DEFAULT_BILLING);
  const isYear = billing === "year";
  const pricing = landingContent.pricing;
  const isComingSoon = siteConfig.ctaMode === "coming-soon";
  const paddlePricing = usePaddlePricing(siteConfig.siteId === "scriptario");
  // Сколько человек ждёт цены, доскроллив до карточек, и не сломались ли они
  // (только Paddle/Scriptario).
  // Карточки тарифов нарисованы дважды: сетка для десктопа и своя раскладка
  // для телефона, лишняя скрыта через display: none. Наблюдать надо за той,
  // что реально видна, — скрытая в окно не попадёт никогда, и замер молча
  // пропал бы для целого класса устройств. Колбэк-ref запоминает видимый узел
  // (у скрытого offsetParent === null).
  const plansGridRef = useRef<HTMLDivElement>(null);
  const setPlansNode = useCallback((node: HTMLDivElement | null) => {
    if (node && node.offsetParent !== null) plansGridRef.current = node;
  }, []);
  usePaywallPriceWaitObservability(plansGridRef, {
    enabled: paddlePricing.enabled,
    pricesReady: paddlePricing.pricesReady,
    failure: paddlePricing.failure,
  });
  const selectedPeriod = isYear ? "year" : "month";

  // На телефоне тариф выбирается сегментами, и на экране живёт один тариф.
  // По умолчанию — первый из данных (Базовый), как в макете владельца.
  const [mobilePlanId, setMobilePlanId] = useState(pricing.plans[0]?.planId ?? "basic");
  const mobilePlan = pricing.plans.find((plan) => plan.planId === mobilePlanId) ?? pricing.plans[0];
  const mobileYearBreakdown = getPlanBreakdown(paddlePricing.prices, mobilePlan.planId, "year");
  const mobileMonthBreakdown = getPlanBreakdown(paddlePricing.prices, mobilePlan.planId, "month");
  const mobileExtraMinuteBreakdown = getExtraMinuteBreakdown(paddlePricing.prices, mobilePlan.planId);
  const mobileYearView = buildPlanPriceView({
    planId: mobilePlan.planId,
    isYear: true,
    priceBreakdown: mobileYearBreakdown,
    monthlyBreakdown: mobileMonthBreakdown,
    extraMinuteBreakdown: mobileExtraMinuteBreakdown,
  });
  const mobileMonthView = buildPlanPriceView({
    planId: mobilePlan.planId,
    isYear: false,
    priceBreakdown: mobileMonthBreakdown,
    extraMinuteBreakdown: mobileExtraMinuteBreakdown,
  });
  const mobileMonthDetails = [mobileMonthView.minutes, mobileMonthView.overage].filter(Boolean).join(" · ");

  const selectPeriod = (value: string) => {
    trackClickPricingToggle(value);
    setBilling(value);
  };


  // Ведущий период занимает большую карточку сверху, второй идёт компактной
  // строкой под ней. Порядок совпадает с DEFAULT_BILLING, чтобы большая
  // карточка соответствовала выбранному по умолчанию периоду.
  const periodMeta = {
    year: { view: mobileYearView, title: pricing.periodYearTitle, badge: pricing.yearBadge },
    month: { view: mobileMonthView, title: pricing.periodMonthTitle, badge: null as string | null },
  } as const;
  const secondBilling = DEFAULT_BILLING === "year" ? "month" : "year";

  const periodInput = (key: "year" | "month") => (
    <input
      type="radio"
      name="pricing-period-mobile"
      value={key}
      checked={billing === key}
      onChange={() => selectPeriod(key)}
      aria-label={periodMeta[key].title}
      className="absolute inset-0 z-[2] size-full cursor-pointer opacity-0"
    />
  );

  const renderBigPeriodCard = (key: "year" | "month") => {
    const { view, title, badge } = periodMeta[key];
    const selected = billing === key;
    return (
      <label
        data-period-card={key}
        className={`relative isolate flex cursor-pointer rounded-[24px] border-[1.5px] bg-white p-[22px] max-[350px]:px-[17px] max-[350px]:py-[20px] focus-within:outline-2 focus-within:outline-offset-[3px] focus-within:outline-[#FFB577] ${selected ? "border-[#FF7900]" : "border-[#F6CDA9]"}`}
      >
        {periodInput(key)}
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-[inherit] opacity-[0.15] pointer-events-none"
          style={{ background: "url('/assets/plan-popular-bg.png') center/cover no-repeat" }}
        />
        <div className="relative z-[1] min-w-0 flex-1 pointer-events-none">
          <div className="flex items-center justify-between gap-[12px] pb-[14px]">
            <h3 className="[font-family:var(--font-commissioner)] text-[16px] font-medium leading-[22px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {title}
            </h3>
            <span className="inline-flex items-center gap-[8px]">
              {badge && (
                <span className="[font-family:var(--font-commissioner)] rounded-[999px] bg-[#FFF3E6] px-[10px] py-[3px] text-[12px] font-semibold leading-[16px] text-[#FF7900] whitespace-nowrap" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
                  {badge}
                </span>
              )}
              <SelectionBox checked={selected} />
            </span>
          </div>
          <div className="flex items-baseline gap-[7px] whitespace-nowrap">
            <span className="[font-family:var(--font-commissioner)] text-[42px] font-bold leading-[48px] tracking-[-0.02em] text-[#1A1D1F] max-[350px]:text-[38px] max-[350px]:leading-[44px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {view.price}
            </span>
            <span className="[font-family:var(--font-commissioner)] text-[16px] leading-[20px] text-[#64748B] max-[350px]:text-[14px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {pricing.perMonth}
            </span>
          </div>
          {view.oldPrice && (
            <div className="[font-family:var(--font-commissioner)] pt-[4px] text-[16px] leading-[20px] text-[#94A3B8] line-through" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {view.oldPrice} {pricing.perMonth}
            </div>
          )}
          {view.yearTotal && (
            <div className="[font-family:var(--font-commissioner)] pt-[8px] text-[13px] leading-[18px] text-[#64748B] max-[350px]:text-[12px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {view.yearTotal} {pricing.yearTotalNote}
            </div>
          )}
          <div className="pt-[16px]">
            <div className="h-[1px] w-full bg-[#E7ECF3]" />
          </div>
          {view.minutes && (
            <div className="flex items-center gap-[8px] pt-[14px] max-[350px]:gap-[7px]">
              <LightningIcon size={16} />
              <span className="[font-family:var(--font-commissioner)] text-[15px] font-semibold leading-[20px] text-[#1A1D1F] max-[350px]:text-[14px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
                {view.minutes}
              </span>
            </div>
          )}
          {view.overage && (
            <div className="[font-family:var(--font-commissioner)] pt-[5px] pl-[24px] text-[13px] leading-[18px] text-[#64748B] max-[350px]:pl-[23px] max-[350px]:text-[12px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {view.overage}
            </div>
          )}
        </div>
      </label>
    );
  };

  const renderSmallPeriodCard = (key: "year" | "month") => {
    const { view, title, badge } = periodMeta[key];
    const selected = billing === key;
    // У года в компактной строке важна сумма одним платежом, у месяца — минуты
    // и перерасход: в большой карточке они стоят отдельными строками.
    const details = key === "year"
      ? (view.yearTotal ? `${view.yearTotal} ${pricing.yearTotalNote}` : "")
      : mobileMonthDetails;
    return (
      <label
        data-period-card={key}
        className={`relative flex min-h-[64px] cursor-pointer items-center rounded-[18px] border-[1.5px] bg-white px-[22px] py-[16px] max-[350px]:px-[17px] max-[350px]:py-[14px] focus-within:outline-2 focus-within:outline-offset-[3px] focus-within:outline-[#FFB577] ${selected ? "border-[#FF7900]" : "border-[var(--brand-primary-soft)]"}`}
      >
        {periodInput(key)}
        <div className="min-w-0 flex-1 pointer-events-none">
          <div className="flex min-h-[26px] items-center justify-between gap-[10px]">
            <h3 className="[font-family:var(--font-commissioner)] text-[16px] font-medium leading-[22px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {title}
            </h3>
            <span className="inline-flex items-center gap-[4px] whitespace-nowrap text-[#64748B]">
              {badge && (
                <span className="[font-family:var(--font-commissioner)] mr-[2px] rounded-[999px] bg-[#FFF3E6] px-[8px] py-[2px] text-[11px] font-semibold leading-[15px] text-[#FF7900]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
                  {badge}
                </span>
              )}
              <span className="[font-family:var(--font-commissioner)] text-[18px] font-medium leading-[24px] max-[350px]:text-[17px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
                {view.price}
              </span>
              <span className="[font-family:var(--font-commissioner)] text-[13px] max-[350px]:text-[12px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
                {pricing.perMonth}
              </span>
              <span className="inline-flex pl-[5px]">
                <SelectionBox checked={selected} />
              </span>
            </span>
          </div>
          {selected && details && (
            <div className="[font-family:var(--font-commissioner)] pt-[10px] text-[12px] leading-[18px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              {details}
            </div>
          )}
        </div>
      </label>
    );
  };

  const openPlanCheckout = (planId: string, planTitle: string) => {
    trackClickTariffSelect(planTitle);
    if (isComingSoon) {
      scrollToAnchor("#pricing");
      return;
    }
    // Период, выбранный здесь, едет в кабинет: иначе чекаут откроется
    // на периоде по умолчанию, а не на том, что человек выбрал.
    const target = featureGuestTranscription
      ? buildAppUrl(`/subscription?checkout=${encodeURIComponent(planId)}&period=${selectedPeriod}`)
      : buildAppUrl(`/login?source=tariff&plan=${encodeURIComponent(planId)}&period=${selectedPeriod}`);
    window.open(appendUtmToUrl(target), "_self");
  };
  const reviewBadge = (
    <>
      <StarIcon />
      {siteConfig.siteId === "scriptario" ? (
        <span className="text-[#64748B]">
          <b className="text-[#1A1D1F] font-bold">5.0</b>&nbsp;{pricing.reviewLabel}
        </span>
      ) : (
        <><b className="text-[#1A1D1F] font-bold">5,0</b>&nbsp;{pricing.reviewLabel}</>
      )}
    </>
  );

  return (
    <section id="pricing" className={`relative flex flex-col items-center gap-[32px] py-[80px] overflow-hidden max-tablet:gap-[20px] max-tablet:pt-[40px] max-tablet:pb-[56px] ${className}`}>
      <div
        className="absolute inset-0 pointer-events-none max-tablet:hidden"
        style={{
          background:
            "radial-gradient(ellipse 62% 56% at 50% 50%, var(--brand-primary-soft) 0%, rgba(255,255,255,0) 72%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none hidden max-tablet:block"
        style={{
          background: "var(--brand-primary-tint)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0px, #000 170px, #000 calc(100% - 120px), transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0px, #000 170px, #000 calc(100% - 120px), transparent 100%)",
        }}
      />

      {/* Title */}
      <div className="relative z-[1] flex flex-col gap-[16px] items-center text-center w-full max-w-[700px] px-[16px]">
        <h2 id="pricing-title" className="[font-family:var(--font-commissioner)] font-bold not-italic text-[40px] leading-[48px] text-[rgba(0,0,0,0.9)] tracking-[-0.02em] max-tablet:text-[28px] max-tablet:leading-[36px] max-tablet:tracking-[-0.56px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
          {pricing.title}
        </h2>
        {lead && (
          <p className="[font-family:var(--font-commissioner)] font-normal not-italic text-[18px] leading-[24px] text-black tracking-[-0.36px] max-tablet:text-[14px] max-tablet:leading-[20px] max-tablet:tracking-[-0.28px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
            {lead}
          </p>
        )}
      </div>

      {/* Social proof bar */}
      <div className="relative z-[1] flex justify-center items-center flex-wrap gap-x-[18px] gap-y-[8px] text-[16px] text-[#64748B] max-tablet:text-[12px] max-tablet:gap-x-[6px] max-tablet:gap-y-[2px]">
        {siteConfig.reviewUrl ? (
          <a
            href={siteConfig.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-[7px] hover:text-[#1A1D1F]"
          >
            {reviewBadge}
          </a>
        ) : (
          <span className="inline-flex items-center gap-[7px]">
            {reviewBadge}
          </span>
        )}
        <span className="text-[#CBD5E1]">•</span>
        <span><b className="text-[#1A1D1F] font-bold">85 000+</b>&nbsp;{pricing.usersLabel}</span>
        {/* На телефоне третий факт уводил блок на две строки: он самый
            длинный. Рейтинг и число людей важнее — объём расшифровок
            остаётся на широком экране. */}
        <span className="text-[#CBD5E1] max-tablet:hidden">•</span>
        <span className="max-tablet:hidden"><b className="text-[#1A1D1F] font-bold">500 000+</b>&nbsp;{pricing.hoursLabel}</span>
      </div>


      {/* Мобильный пейвол: один тариф за раз — сегменты тарифа, две карточки
          периода, одна кнопка. Десктоп рисует прежнюю сетку из трёх карточек. */}
      <div ref={setPlansNode} className="relative z-[1] hidden max-tablet:flex w-full flex-col px-[20px]">
        <div role="group" aria-labelledby="pricing-title" className="grid grid-cols-3 gap-[4px] rounded-[14px] bg-[var(--brand-primary-soft)] p-[4px]">
          {pricing.plans.map((plan) => {
            const active = plan.planId === mobilePlan.planId;
            return (
              <button
                key={plan.planId}
                type="button"
                aria-pressed={active}
                data-plan-tab={plan.planId}
                onClick={() => setMobilePlanId(plan.planId)}
                className={`[font-family:var(--font-commissioner)] flex min-h-[44px] items-center justify-center rounded-[10px] px-[6px] text-center font-medium leading-[1.25] transition-colors cursor-pointer ${active ? "bg-white text-[#1A1D1F]" : "text-[rgba(0,0,0,0.9)]"}`}
                /* Названия тарифов длинные, а вкладка получает треть ширины
                   экрана: тянем кегль по ширине окна, как в кабинете. */
                style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0', fontSize: "clamp(10px, 3.1vw, 13px)" }}
              >
                {plan.title}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-[14px] pt-[18px]">
          {renderBigPeriodCard(DEFAULT_BILLING)}
          {renderSmallPeriodCard(secondBilling)}
        </div>

        <div className="pt-[18px]">
          <button
            type="button"
            onClick={() => openPlanCheckout(mobilePlan.planId, mobilePlan.title)}
            className="[font-family:var(--font-commissioner)] flex min-h-[48px] w-full items-center justify-center rounded-[14px] bg-[var(--brand-primary)] text-[16px] font-semibold leading-[20px] text-white cursor-pointer"
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          >
            {mobilePlan.ctaLabel}
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-[14px] gap-y-[8px] pt-[14px] pb-[20px] text-[12px] leading-[18px] text-[#64748B] max-[350px]:gap-x-[10px] max-[350px]:gap-y-[6px] max-[350px]:text-[11px]">
          <span className="[font-family:var(--font-commissioner)] inline-flex items-center gap-[5px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
            <RefreshIcon />
            {pricing.cancel}
          </span>
          <span className="[font-family:var(--font-commissioner)] inline-flex items-center gap-[5px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
            <ShieldCheckIcon />
            {pricing.refund}
          </span>
        </div>

        <div className="rounded-[16px] border border-[#E7ECF3] bg-white px-[20px] py-[18px] max-[350px]:px-[18px] max-[350px]:py-[16px]">
          <h3 className="[font-family:var(--font-commissioner)] pb-[12px] text-[15px] font-medium leading-[20px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
            {pricing.includedTitle}
          </h3>
          <ul className="grid grid-cols-1 list-none pl-0">
            {pricing.commonFeatures.map((feature, i) => (
              <li
                key={feature}
                className={`[font-family:var(--font-commissioner)] flex min-h-[42px] items-center gap-[10px] text-[14px] leading-[20px] text-[#1A1D1F] max-[350px]:min-h-[40px] max-[350px]:text-[13px] ${i === pricing.commonFeatures.length - 1 ? "" : "border-b border-[#EEF2F7]"}`}
                style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
              >
                <FeatureIcon iconKey={pricing.commonFeatureIcons?.[i]} />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Toggle */}
      <SegmentToggle
        className="relative z-[1] max-tablet:hidden"
        defaultValue={DEFAULT_BILLING}
        onChange={(v) => { trackClickPricingToggle(v); setBilling(v); }}
        options={[{ label: pricing.month, value: "month" }, { label: pricing.year, value: "year", badge: pricing.yearBadge }]}
      />

      <Container className="relative z-[1] flex flex-col gap-[22px]">
      {/* 3 paid cards grid */}
      <div ref={setPlansNode} className="grid grid-cols-3 max-tablet:hidden gap-[22px] w-full items-stretch">
        {pricing.plans.map((plan) => (
          <PricingCard
            key={plan.planId}
            title={plan.title}
            description={plan.description}
            ctaLabel={plan.ctaLabel}
            featured={plan.featured}
            badge={plan.badge}
            planId={plan.planId}
            isYear={isYear}
            priceBreakdown={getPlanBreakdown(paddlePricing.prices, plan.planId, selectedPeriod)}
            monthlyBreakdown={isYear ? getPlanBreakdown(paddlePricing.prices, plan.planId, "month") : null}
            extraMinuteBreakdown={getExtraMinuteBreakdown(paddlePricing.prices, plan.planId)}
          />
        ))}
      </div>

      {/* Free row */}
      <div className="flex items-center justify-between gap-x-[16px] gap-y-[12px] bg-white border border-[var(--brand-primary-soft)] rounded-[16px] px-[22px] py-[16px] w-full max-tablet:flex-col max-tablet:items-start max-tablet:gap-y-[10px] hover:bg-[var(--brand-primary-tint)] hover:border-[var(--brand-primary-lighter)] transition-colors duration-200">
        <div className="flex items-center gap-[12px]">
          <div className="size-[34px] rounded-[9px] bg-[var(--brand-primary-tint)] flex items-center justify-center shrink-0">
            <LightningIcon />
          </div>
          <span className="[font-family:var(--font-commissioner)] inline-flex items-baseline gap-[10px] text-[14px] leading-[18px] text-[#64748B]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
            <b className="text-[#1A1D1F] font-bold">{pricing.tryTitle}</b>
            <span>{pricing.tryDescription}</span>
          </span>
        </div>
        <span
          className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[14px] leading-[18px] text-[var(--brand-primary)] whitespace-nowrap cursor-pointer hover:underline px-[8px] py-[4px] -mr-[8px] rounded-[8px] max-tablet:mr-0 max-tablet:ml-0 max-tablet:self-center"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          onClick={() => {
            if (isComingSoon) {
              scrollToAnchor("#pricing");
              return;
            }
            window.open(appendUtmToUrl(buildAppUrl(`/login?source=tariff&plan=${encodeURIComponent(siteConfig.siteId === "scriptario" ? "Free" : "Бесплатный")}`)), "_self");
          }}
        >
          {pricing.startFree}
        </span>
      </div>

      {/* "В каждом тарифе" section */}
      <div className="flex justify-center items-center flex-wrap gap-x-[24px] gap-y-[12px] bg-[var(--brand-primary-tint)] rounded-[16px] px-[24px] py-[18px] w-full max-tablet:hidden">
        <span className="[font-family:var(--font-commissioner)] font-bold not-italic text-[15px] leading-[20px] text-[#1A1D1F]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>{pricing.includedTitle}</span>
        <div className="contents max-tablet:grid max-tablet:grid-cols-2 max-tablet:w-full max-tablet:gap-x-[16px] max-tablet:gap-y-[10px]">
          {pricing.commonFeatures.map((feature, i) => (
            <span key={feature} className={`[font-family:var(--font-commissioner)] inline-flex items-center gap-[8px] text-[15px] text-[#1A1D1F] whitespace-nowrap max-tablet:justify-self-center ${i === pricing.commonFeatures.length - 1 && pricing.commonFeatures.length % 2 !== 0 ? 'max-tablet:col-span-2 max-tablet:justify-self-center' : ''}`} style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
              <CheckIcon />
              {feature}
            </span>
          ))}
        </div>
      </div>
      </Container>

      {/* Guarantee */}
      <div className="relative z-[1] flex justify-center flex-wrap gap-x-[34px] gap-y-[14px] text-[14px] text-[#64748B] max-tablet:hidden">
        <span className="[font-family:var(--font-commissioner)] inline-flex items-center gap-[8px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
          <RefreshIcon />
          {pricing.cancel}
        </span>
        <span className="[font-family:var(--font-commissioner)] inline-flex items-center gap-[8px]" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
          <ShieldCheckIcon />
          {pricing.refund}
        </span>
      </div>
    </section>
  );
}
